import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, ShieldCheck, ShieldAlert, RefreshCw, Plus, Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => {
  const t = localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DEFAULT_DOMAIN = 'objectifscopoutremer.com';

function DnsRecordRow({ label, value, type }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };
  return (
    <div className="flex items-start gap-2 py-2 border-b border-slate-100 last:border-0">
      <div className="w-20 shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
        {type && <div className="text-[10px] text-slate-400">{type}</div>}
      </div>
      <code className="flex-1 text-[11px] font-mono break-all bg-slate-50 px-2 py-1 rounded border border-slate-200 text-slate-800">
        {value}
      </code>
      <Button size="sm" variant="ghost" onClick={onCopy} className="h-7 px-2 shrink-0" data-testid={`brevo-dns-copy-${label}`}>
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-slate-500" />}
      </Button>
    </div>
  );
}

export default function BrevoDomainWidget() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [domainInput, setDomainInput] = useState(DEFAULT_DOMAIN);
  const [dnsRecords, setDnsRecords] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/seed/brevo/account-info`, { headers: getAuthHeader() });
      setInfo(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Impossible de charger l'état Brevo : ${detail}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const verifiedDomain = info?.domains?.find((d) => d.domain === domainInput && d.verified);
  const unverifiedDomain = info?.domains?.find((d) => d.domain === domainInput && !d.verified);

  const setupDomain = async () => {
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/admin/seed/brevo/setup-domain`, { domain: domainInput }, { headers: getAuthHeader() });
      setDnsRecords(r.data.dns_records);
      toast.success(`Domaine ${domainInput} enregistré chez Brevo. Ajoutez les enregistrements DNS ci-dessous.`, { duration: 8000 });
      fetchInfo();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement domaine');
    } finally {
      setVerifying(false);
    }
  };

  const verifyDomain = async () => {
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/admin/seed/brevo/verify-domain`, { domain: domainInput }, { headers: getAuthHeader() });
      if (r.data.status === 'verified') {
        toast.success(`✅ Domaine ${domainInput} vérifié ! Les emails partiront maintenant nativement.`, { duration: 8000 });
      }
      fetchInfo();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      toast.error(`Vérification échouée : ${detail.slice(0, 200)}`, { duration: 9000, description: 'Vérifiez que les enregistrements DNS sont propagés (~5-30 min).' });
    } finally {
      setVerifying(false);
    }
  };

  const senderConfigured = info?.configured_sender_email;
  const senderIsVerified = info?.senders?.some((s) => s.email === senderConfigured && s.active);

  return (
    <Card data-testid="brevo-domain-widget" className="overflow-hidden border-slate-200 hover:shadow-lg transition-all">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className={`lg:col-span-3 bg-gradient-to-br ${senderIsVerified || verifiedDomain ? 'from-emerald-500 to-teal-600' : 'from-amber-500 to-orange-600'} text-white p-6 flex flex-col items-center justify-center text-center`}>
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <Mail className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Brevo</span>
            </div>
            <div className="w-24 h-24 rounded-full bg-white/15 ring-4 ring-white/30 flex items-center justify-center backdrop-blur-sm">
              {senderIsVerified || verifiedDomain ? <ShieldCheck className="h-10 w-10" /> : <ShieldAlert className="h-10 w-10" />}
            </div>
            <div className="mt-3 text-xs font-bold">
              {info?.plan_credits != null ? `${info.plan_credits.toLocaleString('fr-FR')} crédits` : '—'}
            </div>
            <div className="text-[10px] opacity-80">{info?.plan_type || ''}</div>
          </div>

          <div className="lg:col-span-9 p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">État Brevo & vérification de domaine</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Compte : <code className="font-mono">{info?.account_email || '—'}</code> ·
                  {' '}Sender configuré : <code className="font-mono">{senderConfigured || '—'}</code>
                  {senderConfigured && (
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${senderIsVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
                      {senderIsVerified ? 'Vérifié' : 'Non vérifié → fallback actif'}
                    </span>
                  )}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={fetchInfo} disabled={loading} data-testid="brevo-refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {!senderIsVerified && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  Pour envoyer nativement depuis <code>{senderConfigured}</code>, vérifiez le domaine
                  ci-dessous (DKIM/SPF/DMARC). Sans cela, le système utilise un sender de fallback Brevo.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] font-bold uppercase text-slate-500">Senders vérifiés</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{info?.senders_count ?? '—'}</div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] font-bold uppercase text-slate-500">Domaines</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{info?.domains_count ?? '—'}</div>
                <div className="text-[10px] text-slate-500">{info?.domains?.filter((d) => d.verified).length || 0} vérifié(s)</div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] font-bold uppercase text-slate-500">Crédits emails</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{info?.plan_credits?.toLocaleString('fr-FR') || '—'}</div>
              </div>
            </div>

            {/* Domaine setup */}
            <div className="rounded-lg border border-slate-200 p-3 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Vérifier un domaine</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="ex: objectifscopoutremer.com"
                  className="text-sm"
                  data-testid="brevo-domain-input"
                />
                {!unverifiedDomain && !verifiedDomain && (
                  <Button onClick={setupDomain} disabled={verifying || !domainInput} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="brevo-setup-domain-btn">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Enregistrer
                  </Button>
                )}
                {unverifiedDomain && (
                  <Button onClick={verifyDomain} disabled={verifying} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="brevo-verify-domain-btn">
                    {verifying ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                    Lancer la vérification
                  </Button>
                )}
                {verifiedDomain && (
                  <span className="inline-flex items-center px-3 py-2 text-xs font-bold rounded-md bg-emerald-100 text-emerald-700 gap-1.5">
                    <ShieldCheck className="h-4 w-4" /> Vérifié
                  </span>
                )}
              </div>

              {/* DNS records à coller */}
              {dnsRecords && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-700 font-semibold mb-2">📋 Ajoutez ces enregistrements chez votre registrar DNS{dnsRecords.domain_provider ? ` (${dnsRecords.domain_provider} détecté)` : ''}, puis cliquez "Lancer la vérification" :</p>
                  <div className="space-y-1">
                    {/* Format Brevo : dns_records.{dkim1Record, dkim2Record, brevo_code, dmarc_record} */}
                    {dnsRecords.dns_records && Object.entries(dnsRecords.dns_records).map(([key, rec]) => (
                      <div key={key} className="rounded-md border border-slate-200 bg-slate-50 p-2 mb-2">
                        <div className="flex items-start gap-2">
                          <div className="w-24 shrink-0">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">{key.replace('Record', '').replace('_record', '')}</div>
                            <div className="text-[10px] text-slate-500">Type {rec.type}</div>
                            {rec.status && <div className="text-[10px] text-emerald-600 font-bold">✓ Vérifié</div>}
                          </div>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold uppercase text-slate-500 w-12 shrink-0">Host</span>
                              <code className="flex-1 text-[11px] font-mono break-all bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-800">{rec.host_name}</code>
                              <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(rec.host_name)} className="h-6 px-1.5 shrink-0">
                                <Copy className="h-3 w-3 text-slate-500" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold uppercase text-slate-500 w-12 shrink-0">Value</span>
                              <code className="flex-1 text-[11px] font-mono break-all bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-800">{rec.value}</code>
                              <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(rec.value)} className="h-6 px-1.5 shrink-0" data-testid={`brevo-dns-copy-${key}`}>
                                <Copy className="h-3 w-3 text-slate-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2">
                    💡 Propagation DNS : 5–30 min selon votre registrar. Une fois propagés, cliquez "Lancer la vérification".
                  </p>
                </div>
              )}
            </div>

            {/* Liste senders */}
            {info?.senders?.length > 0 && (
              <details className="rounded-lg border border-slate-200 bg-white p-3">
                <summary className="text-xs font-bold uppercase tracking-wider text-slate-700 cursor-pointer">
                  Senders enregistrés ({info.senders.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {info.senders.map((s) => (
                    <li key={s.email} className="flex items-center justify-between py-1 px-2 rounded bg-slate-50 text-xs">
                      <code className="text-slate-800">{s.email}</code>
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${s.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                        {s.active ? 'Actif' : 'Inactif'}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
